import { useMemo, useState } from 'react'
import type { FormLogicRule, FormTemplateField } from '@/types'
import { FormLogicAction, FormLogicOperator } from '@/types'
import { normalizeKeyword } from './fieldFilters'
import type {
  CreateLogicRulePayload,
  UpdateLogicRulePayload,
} from '@/services/api/formTemplates'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface LogicRulePanelProps {
  rules: FormLogicRule[]
  fields: FormTemplateField[]
  filterKeyword?: string
  onCreateRule: (payload: CreateLogicRulePayload) => Promise<void>
  onUpdateRule: (ruleId: string, payload: UpdateLogicRulePayload) => Promise<void>
  onDeleteRule: (ruleId: string) => Promise<void>
  onReorderRules: (orderedRuleIds: string[]) => Promise<void>
}

interface LogicRuleFormState {
  triggerFieldId: string
  effectFieldId: string
  operator: FormLogicOperator
  action: FormLogicAction
  comparisonValue: string
}

const operatorLabels: Record<FormLogicOperator, string> = {
  [FormLogicOperator.EQUALS]: '等於',
  [FormLogicOperator.NOT_EQUALS]: '不等於',
  [FormLogicOperator.GREATER_THAN]: '大於',
  [FormLogicOperator.GREATER_OR_EQUAL]: '大於等於',
  [FormLogicOperator.LESS_THAN]: '小於',
  [FormLogicOperator.LESS_OR_EQUAL]: '小於等於',
  [FormLogicOperator.CONTAINS]: '包含',
  [FormLogicOperator.NOT_CONTAINS]: '不包含',
  [FormLogicOperator.IN]: '在列表中',
  [FormLogicOperator.NOT_IN]: '不在列表中',
  [FormLogicOperator.IS_EMPTY]: '為空',
  [FormLogicOperator.IS_NOT_EMPTY]: '不為空',
}

const actionLabels: Record<FormLogicAction, string> = {
  [FormLogicAction.SHOW]: '顯示',
  [FormLogicAction.HIDE]: '隱藏',
  [FormLogicAction.ENABLE]: '啟用',
  [FormLogicAction.DISABLE]: '停用',
  [FormLogicAction.REQUIRE]: '設為必填',
  [FormLogicAction.OPTIONAL]: '設為選填',
  [FormLogicAction.SET_VALUE]: '設定值',
  [FormLogicAction.CLEAR_VALUE]: '清除值',
}

const defaultFormState: LogicRuleFormState = {
  triggerFieldId: '',
  effectFieldId: '',
  operator: FormLogicOperator.EQUALS,
  action: FormLogicAction.SHOW,
  comparisonValue: '',
}

const resolveErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.length > 0) {
      return message
    }
  }
  return fallback
}

const LogicRulePanel = ({
  rules,
  fields,
  filterKeyword,
  onCreateRule,
  onUpdateRule,
  onDeleteRule,
  onReorderRules,
}: LogicRulePanelProps) => {
  const keyword = normalizeKeyword(filterKeyword ?? '')
  const fieldById = useMemo(() => {
    const map: Record<string, FormTemplateField> = {}
    fields.forEach(field => {
      map[field.id] = field
    })
    return map
  }, [fields])

  const sortedRules = useMemo(
    () =>
      [...rules]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id.localeCompare(b.id)),
    [rules],
  )

  const [editorOpen, setEditorOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [formState, setFormState] = useState<LogicRuleFormState>(defaultFormState)
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null)

  const matchesKeyword = (rule: FormLogicRule) => {
    if (!keyword) return true
    const trigger = fieldById[rule.triggerFieldId]?.label ?? ''
    const effect = fieldById[rule.effectFieldId]?.label ?? ''
    const content = `${trigger} ${effect} ${operatorLabels[rule.operator] ?? ''} ${actionLabels[rule.action] ?? ''} ${rule.comparisonValue ?? ''}`
    return content.toLowerCase().includes(keyword)
  }

  const visibleRules = sortedRules.filter(matchesKeyword)
  const isFiltering = keyword.length > 0

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const currentOrderIds = sortedRules.map(rule => rule.id)

  const handleDragEnd = async (event: DragEndEvent) => {
    if (isFiltering) return
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = currentOrderIds.indexOf(active.id as string)
    const newIndex = currentOrderIds.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    const newOrder = arrayMove(currentOrderIds, oldIndex, newIndex)
    await onReorderRules(newOrder)
  }

  const openCreateEditor = () => {
    setFormState(defaultFormState)
    setEditingRuleId(null)
    setFormError(null)
    setEditorOpen(true)
  }

  const openEditEditor = (rule: FormLogicRule) => {
    setFormState({
      triggerFieldId: rule.triggerFieldId,
      effectFieldId: rule.effectFieldId,
      operator: rule.operator,
      action: rule.action,
      comparisonValue:
        rule.comparisonValue === undefined || rule.comparisonValue === null
          ? ''
          : String(rule.comparisonValue),
    })
    setEditingRuleId(rule.id)
    setFormError(null)
    setEditorOpen(true)
  }

  const closeEditor = () => {
    setEditorOpen(false)
    setFormError(null)
    setFormState(defaultFormState)
    setEditingRuleId(null)
  }

  const handleSubmit = async () => {
    if (!formState.triggerFieldId || !formState.effectFieldId) {
      setFormError('請選擇觸發欄位與影響欄位')
      return
    }

    const comparisonValue = formState.comparisonValue.trim()

    try {
      setIsSubmitting(true)
      if (editingRuleId) {
        const payload: UpdateLogicRulePayload = {
          triggerFieldId: formState.triggerFieldId,
          effectFieldId: formState.effectFieldId,
          operator: formState.operator,
          action: formState.action,
          comparisonValue: comparisonValue === '' ? null : comparisonValue,
        }
        await onUpdateRule(editingRuleId, payload)
      } else {
        const payload: CreateLogicRulePayload = {
          triggerFieldId: formState.triggerFieldId,
          effectFieldId: formState.effectFieldId,
          operator: formState.operator,
          action: formState.action,
          comparisonValue: comparisonValue === '' ? undefined : comparisonValue,
          position: sortedRules.length,
        }
        await onCreateRule(payload)
      }
      closeEditor()
    } catch (error: unknown) {
      setFormError(resolveErrorMessage(error, '儲存條件邏輯失敗'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDuplicate = async (rule: FormLogicRule) => {
    try {
      const index = sortedRules.findIndex(item => item.id === rule.id)
      await onCreateRule({
        triggerFieldId: rule.triggerFieldId,
        effectFieldId: rule.effectFieldId,
        operator: rule.operator,
        action: rule.action,
        comparisonValue:
          rule.comparisonValue === undefined || rule.comparisonValue === null
            ? undefined
            : rule.comparisonValue,
        position: index === -1 ? sortedRules.length : index + 1,
      })
    } catch (error: unknown) {
      setFormError(resolveErrorMessage(error, '複製條件邏輯失敗'))
    }
  }

  const handleDelete = async (ruleId: string) => {
    if (!window.confirm('確定要刪除此條件邏輯嗎？')) return
    try {
      await onDeleteRule(ruleId)
    } catch (error: unknown) {
      setFormError(resolveErrorMessage(error, '刪除條件邏輯失敗'))
    }
  }

  const fieldOptions = fields
    .slice()
    .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))

  const operatorOptions = Object.values(FormLogicOperator)
  const actionOptions = Object.values(FormLogicAction)

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">條件邏輯</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{rules.length} 條</span>
          <button
            type="button"
            onClick={openCreateEditor}
            className="rounded border border-primary-500 px-2 py-1 text-[11px] text-primary-600 hover:bg-primary-50"
          >
            新增條件
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto rounded-md border border-gray-200 bg-white">
        {visibleRules.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">
            {keyword ? '沒有符合搜尋的條件邏輯' : '尚未設定條件邏輯'}
          </div>
        ) : isFiltering ? (
          <ul className="divide-y divide-gray-200">
            {visibleRules.map(rule => (
              <RuleItem
                key={rule.id}
                rule={rule}
                triggerLabel={fieldById[rule.triggerFieldId]?.label ?? '未知欄位'}
                effectLabel={fieldById[rule.effectFieldId]?.label ?? '未知欄位'}
                onEdit={openEditEditor}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                dragHandle={false}
              />
            ))}
          </ul>
        ) : (
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <SortableContext items={currentOrderIds} strategy={verticalListSortingStrategy}>
              <ul className="divide-y divide-gray-200">
                {sortedRules.map(rule => (
                  <SortableRuleItem
                    key={rule.id}
                    rule={rule}
                    triggerLabel={fieldById[rule.triggerFieldId]?.label ?? '未知欄位'}
                    effectLabel={fieldById[rule.effectFieldId]?.label ?? '未知欄位'}
                    onEdit={openEditEditor}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
        {isFiltering && (
          <p className="px-4 py-2 text-xs text-gray-400">搜尋模式下暫停拖曳排序</p>
        )}
      </div>
      <p className="mt-3 text-xs text-gray-400">※ 條件邏輯目前為 Beta 功能，請儲存後重新確認欄位效果。</p>

      {editorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
            <h4 className="text-base font-semibold text-gray-800">
              {editingRuleId ? '編輯條件邏輯' : '新增條件邏輯'}
            </h4>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">觸發欄位</label>
                <select
                  value={formState.triggerFieldId}
                  onChange={(e) => setFormState(prev => ({ ...prev, triggerFieldId: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">選擇欄位</option>
                  {fieldOptions.map(field => (
                    <option key={field.id} value={field.id}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">影響欄位</label>
                <select
                  value={formState.effectFieldId}
                  onChange={(e) => setFormState(prev => ({ ...prev, effectFieldId: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">選擇欄位</option>
                  {fieldOptions.map(field => (
                    <option key={field.id} value={field.id}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">條件運算符</label>
                  <select
                    value={formState.operator}
                    onChange={(e) => setFormState(prev => ({ ...prev, operator: e.target.value as FormLogicOperator }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    {operatorOptions.map(option => (
                      <option key={option} value={option}>
                        {operatorLabels[option]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">動作</label>
                  <select
                    value={formState.action}
                    onChange={(e) => setFormState(prev => ({ ...prev, action: e.target.value as FormLogicAction }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    {actionOptions.map(option => (
                      <option key={option} value={option}>
                        {actionLabels[option]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">比較值（可選）</label>
                <input
                  type="text"
                  value={formState.comparisonValue}
                  onChange={(e) => setFormState(prev => ({ ...prev, comparisonValue: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="輸入比較值或留白"
                />
              </div>
            </div>
            {formError && <p className="mt-3 text-xs text-red-600">{formError}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeEditor}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                disabled={isSubmitting}
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-70"
              >
                {isSubmitting ? '儲存中…' : '儲存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const RuleItem = ({
  rule,
  triggerLabel,
  effectLabel,
  onEdit,
  onDuplicate,
  onDelete,
  dragHandle,
  listeners,
  attributes,
}: {
  rule: FormLogicRule
  triggerLabel: string
  effectLabel: string
  onEdit: (rule: FormLogicRule) => void
  onDuplicate: (rule: FormLogicRule) => void
  onDelete: (ruleId: string) => void
  dragHandle: boolean
  listeners?: Record<string, unknown>
  attributes?: Record<string, unknown>
}) => (
  <li className="px-3 py-2 text-xs text-gray-600">
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        {dragHandle ? (
          <button
            type="button"
            className="flex h-6 w-6 cursor-grab items-center justify-center rounded border border-gray-200 bg-gray-50 text-gray-400"
            {...listeners}
            {...attributes}
          >
            <span className="text-xs">↕</span>
          </button>
        ) : null}
        <div>
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-700">{actionLabels[rule.action] ?? rule.action}</span>
            <span>→</span>
            <span className="text-gray-800">{effectLabel}</span>
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            觸發：{triggerLabel}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            條件：{operatorLabels[rule.operator] ?? rule.operator}
            {rule.comparisonValue !== undefined && rule.comparisonValue !== null && rule.comparisonValue !== '' ? (
              <span> {String(rule.comparisonValue)}</span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onEdit(rule)}
          className="rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-100"
        >
          編輯
        </button>
        <button
          type="button"
          onClick={() => onDuplicate(rule)}
          className="rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-100"
        >
          複製
        </button>
        <button
          type="button"
          onClick={() => onDelete(rule.id)}
          className="rounded border border-red-200 px-1.5 py-0.5 text-[11px] text-red-600 hover:bg-red-50"
        >
          刪除
        </button>
      </div>
    </div>
  </li>
)

const SortableRuleItem = ({
  rule,
  triggerLabel,
  effectLabel,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  rule: FormLogicRule
  triggerLabel: string
  effectLabel: string
  onEdit: (rule: FormLogicRule) => void
  onDuplicate: (rule: FormLogicRule) => void
  onDelete: (ruleId: string) => void
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'z-10' : undefined}>
      <RuleItem
        rule={rule}
        triggerLabel={triggerLabel}
        effectLabel={effectLabel}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        dragHandle
        listeners={listeners as unknown as Record<string, unknown>}
        attributes={attributes as unknown as Record<string, unknown>}
      />
    </div>
  )
}

export default LogicRulePanel
